// apps/web/src/routes/AppRoutes.tsx
// Complete web application routing for the multi-tenant platform.
//
// Route map (mirrors reference/urls.md UI section):
//   - Public: /, /signin, /signup, /@:handle, /health-check
//   - Authenticated: /dashboard, /settings/*, /admin/users
//   - Platform managers under /app/* (RequireAuth + admin where noted)
//
// Every /app/* route is server-verified via <RequireAuth> — the backend
// /api/v1/user/me check is the load-bearing boundary; these wrappers only
// avoid flashing protected content before the redirect fires.

import { createElement, type ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Link } from 'react-router-dom';

// Phase 2 pages (reference/phase-1-5)
import { Landing } from './Landing.js';
import { SignIn } from './SignIn.js';
import { SignUp } from './SignUp.js';
import { PublicProfile } from './PublicProfile.js';
import { Dashboard } from './Dashboard.js';
import { ProfileSettings } from './ProfileSettings.js';
import { SecuritySettings } from './SecuritySettings.js';
import { NotificationSettings } from './NotificationSettings.js';
import { AdminUsers } from './AdminUsers.js';
import { HealthCheck } from './health-check.js';

// Guards
import { RequireAuth, RequireAdmin } from '../components/RequireAuth.js';

// Platform managers
import { BrandManager } from '../components/brands/BrandManager.js';
import { BrandSafetyDashboard } from '../components/brand-safety/BrandSafetyDashboard.js';
import { CampaignAnalytics } from '../components/campaigns/CampaignAnalytics.js';
import { ContentManager } from '../components/content/ContentManager.js';
import { ContentLibrary } from '../components/content/ContentLibrary.js';
import { CreatorManagement } from '../components/creators/CreatorManagement.js';
import { CreatorDiscovery } from '../components/creators/CreatorDiscovery.js';
import { DeliverableManager } from '../components/deliverables/DeliverableManager.js';
import { DisputesManager } from '../components/disputes/DisputesManager.js';
import { DocumentsManager } from '../components/documents/DocumentsManager.js';
import { FileManager } from '../components/files/FileManager.js';
import { FinanceDashboard } from '../components/finance/FinanceDashboard.js';
import { InvoiceManager } from '../components/finance/InvoiceManager.js';
import { HelpCenter } from '../components/help/HelpCenter.js';
import { IntegrationManager } from '../components/integrations/IntegrationManager.js';
import { IssuesManager } from '../components/issues/IssuesManager.js';
import { OnboardingFlow } from '../components/onboarding/OnboardingFlow.js';
import { PrivacyManager } from '../components/privacy/PrivacyManager.js';
import { ProgrammeManager } from '../components/programmes/ProgrammeManager.js';
import { ReportsManager } from '../components/reports/ReportsManager.js';
import { RightsDashboard } from '../components/rights/RightsDashboard.js';
import { RightsManager } from '../components/rights/RightsManager.js';
import { SecurityManager } from '../components/security/SecurityManager.js';
import { TeamManager } from '../components/teams/TeamManager.js';
import { TenantList } from '../components/tenants/TenantList.js';
import { ToolsManager } from '../components/tools/ToolsManager.js';
import { WorkspaceManager } from '../components/workspaces/WorkspaceManager.js';
import { CompleteDashboard } from '../components/dashboard/CompleteDashboard.js';
import { MainNavigation } from '../components/navigation/MainNavigation.js';
import { Footer } from '../components/layout/Footer.js';
import ErrorBoundary from '../components/common/ErrorBoundary.js';

// Tenant-scoped platform shell + new managers
import { TenantApp, FlatAppRedirect } from './TenantApp.js';
import { TenantRouteLayout } from './TenantRouteLayout.js';
import { tenantLoader } from './tenantLoader.js';
import { moduleRoutes } from '../app/moduleRegistry.js';
import { AgreementsManager } from '../components/agreements/AgreementsManager.js';
import { OffersManager } from '../components/offers/OffersManager.js';
import { OrganisationManager } from '../components/organisations/OrganisationManager.js';
import { PartyManager } from '../components/parties/PartyManager.js';
import { SettlementManager } from '../components/settlements/SettlementManager.js';
import { PaymentsManager } from '../components/payments/PaymentsManager.js';
import { SocialAccountsManager } from '../components/social-accounts/SocialAccountsManager.js';
import { WebhooksManager } from '../components/webhooks/WebhooksManager.js';
import { PaymentAccountsManager } from '../components/payment-accounts/PaymentAccountsManager.js';
import { ApiKeysManager } from '../components/api-keys/ApiKeysManager.js';
import { SubscriptionsManager } from '../components/subscriptions/SubscriptionsManager.js';
import { TaxManager } from '../components/tax/TaxManager.js';
import { NotificationsManager } from '../components/notifications/NotificationsManager.js';
import { AuditLogsViewer } from '../components/audit-logs/AuditLogsViewer.js';
import { ConsentsManager } from '../components/consents/ConsentsManager.js';
import { ApiDocs } from '../components/docs/ApiDocs.js';
import { BackupManager } from '../components/backup/BackupManager.js';
import { MonitoringDashboard } from '../components/monitoring/MonitoringDashboard.js';
import { SearchResults } from '../components/search/SearchResults.js';
import { BreachDetection } from '../components/breach/BreachDetection.js';
import { ProfileModule } from '../components/profile/ProfileModule.js';
import { UsersModule } from '../components/users/UsersModule.js';
import { StatusModule } from '../components/status/StatusModule.js';
import { MobileModule } from '../components/mobile/MobileModule.js';
import {
  Page401,
  Page403,
  Page404,
  Page429,
  Page500,
  Page503,
  AccessDenied,
  AccountLocked,
  AcceptInvite,
} from './PublicPages.js';
import { standaloneElements } from './StandalonePages.js';

// -----------------------------------------------------------------------------
// Layouts
// -----------------------------------------------------------------------------

function PlatformLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="platform-layout">
      <MainNavigation />
      <main className="platform-main">{children}</main>
      <Footer />
    </div>
  );
}

function requirePlatform(children: ReactNode): JSX.Element {
  return (
    <RequireAuth>
      <PlatformLayout>{children}</PlatformLayout>
    </RequireAuth>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="not-found">
      <h1>404</h1>
      <p>This page doesn&apos;t exist.</p>
      <Link to="/" className="btn btn-primary">
        Go home
      </Link>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Router
// -----------------------------------------------------------------------------

export const router = createBrowserRouter([
  // Public routes
  { path: '/', element: <Landing /> },
  { path: '/signin', element: <SignIn /> },
  { path: '/signup', element: <SignUp /> },
  { path: '/@:handle', element: <PublicProfile /> },
  { path: '/health-check', element: <HealthCheck /> },
  { path: '/status', element: <StatusModule /> },

  // Authenticated core pages
  { path: '/dashboard', element: <RequireAuth><Dashboard /></RequireAuth> },
  { path: '/settings/profile', element: <RequireAuth><ProfileSettings /></RequireAuth> },
  { path: '/settings/security', element: <RequireAuth><SecuritySettings /></RequireAuth> },
  { path: '/settings/notifications', element: <RequireAuth><NotificationSettings /></RequireAuth> },

  // Admin
  { path: '/admin/users', element: <RequireAdmin><AdminUsers /></RequireAdmin> },

  // Platform managers (flat, non-slug /app/*)
  { path: '/app', element: requirePlatform(<CompleteDashboard />) },
  { path: '/app/dashboard', element: requirePlatform(<CompleteDashboard />) },
  { path: '/app/brands', element: requirePlatform(<BrandManager />) },
  { path: '/app/brand-safety', element: requirePlatform(<BrandSafetyDashboard />) },
  { path: '/app/campaigns', element: requirePlatform(<CampaignAnalytics campaignId="current" />) },
  { path: '/app/content', element: requirePlatform(<ContentLibrary />) },
  { path: '/app/content/manage', element: requirePlatform(<ContentManager tenantId="current" />) },
  { path: '/app/creators', element: requirePlatform(<CreatorManagement tenantId="current" />) },
  { path: '/app/creators/discovery', element: requirePlatform(<CreatorDiscovery />) },
  { path: '/app/deliverables', element: requirePlatform(<DeliverableManager campaignId="current" />) },
  { path: '/app/disputes', element: requirePlatform(<DisputesManager />) },
  { path: '/app/documents', element: requirePlatform(<DocumentsManager />) },
  { path: '/app/files', element: requirePlatform(<FileManager />) },
  { path: '/app/finance', element: requirePlatform(<FinanceDashboard />) },
  { path: '/app/finance/invoices', element: requirePlatform(<InvoiceManager />) },
  { path: '/app/help', element: requirePlatform(<HelpCenter />) },
  { path: '/app/integrations', element: requirePlatform(<IntegrationManager />) },
  { path: '/app/issues', element: requirePlatform(<IssuesManager />) },
  { path: '/app/mobile', element: requirePlatform(<MobileModule />) },
  { path: '/app/onboarding', element: requirePlatform(<OnboardingFlow />) },
  { path: '/app/privacy', element: requirePlatform(<PrivacyManager />) },
  { path: '/app/programmes', element: requirePlatform(<ProgrammeManager />) },
  { path: '/app/reports', element: requirePlatform(<ReportsManager />) },
  { path: '/app/rights', element: requirePlatform(<RightsDashboard />) },
  { path: '/app/rights/manage', element: requirePlatform(<RightsManager contentId="current" />) },
  { path: '/app/security', element: requirePlatform(<SecurityManager />) },
  { path: '/app/teams', element: requirePlatform(<TeamManager />) },
  { path: '/app/tenants', element: requirePlatform(<TenantList onTenantSelect={() => {}} />) },
  { path: '/app/tools', element: requirePlatform(<ToolsManager />) },
  { path: '/app/workspaces', element: requirePlatform(<WorkspaceManager />) },

  // Extended managers (flat /app/*)
  { path: '/app/agreements', element: requirePlatform(<AgreementsManager />) },
  { path: '/app/offers', element: requirePlatform(<OffersManager />) },
  { path: '/app/organisations', element: requirePlatform(<OrganisationManager />) },
  { path: '/app/parties', element: requirePlatform(<PartyManager />) },
  { path: '/app/settlements', element: requirePlatform(<SettlementManager />) },
  { path: '/app/payments', element: requirePlatform(<PaymentsManager />) },
  { path: '/app/payment-accounts', element: requirePlatform(<PaymentAccountsManager />) },
  { path: '/app/social-accounts', element: requirePlatform(<SocialAccountsManager />) },
  { path: '/app/webhooks', element: requirePlatform(<WebhooksManager />) },
  { path: '/app/api-keys', element: requirePlatform(<ApiKeysManager />) },
  { path: '/app/subscriptions', element: requirePlatform(<SubscriptionsManager />) },
  { path: '/app/tax', element: requirePlatform(<TaxManager />) },
  { path: '/app/notifications', element: requirePlatform(<NotificationsManager />) },
  { path: '/app/audit-logs', element: requirePlatform(<AuditLogsViewer />) },
  { path: '/app/consents', element: requirePlatform(<ConsentsManager />) },
  { path: '/app/docs', element: requirePlatform(<ApiDocs />) },
  { path: '/app/backup', element: requirePlatform(<BackupManager />) },
  { path: '/app/monitoring', element: requirePlatform(<MonitoringDashboard />) },
  { path: '/app/search', element: requirePlatform(<SearchResults />) },
  { path: '/app/breach-detection', element: requirePlatform(<BreachDetection />) },
  { path: '/app/profile', element: requirePlatform(<ProfileModule />) },
  { path: '/app/users', element: requirePlatform(<UsersModule />) },
  { path: '/app/status', element: requirePlatform(<StatusModule />) },

  // Tenant-scoped explicit feature routes.
  {
    path: '/app/:tenantSlug',
    loader: tenantLoader,
    element: <RequireAuth><TenantRouteLayout /></RequireAuth>,
    errorElement: <ErrorBoundary><NotFound /></ErrorBoundary>,
    children: moduleRoutes(),
  },

  // Compatibility fallback for modules not yet migrated to explicit route trees.
  {
    path: '/app/:tenantSlug/*',
    element: requirePlatform(<TenantApp />),
  },
  { path: '/app/*', element: requirePlatform(<FlatAppRedirect />) },

  // Standalone module pages from reference/url1.md (auth, mfa, legal, system).
  // The element is a component reference (NOT called) — react-router calls
  // it lazily when the route matches. Calling eagerly would evaluate
  // components outside a React render context and trip the
  // "Invalid hook call" guard.
  ...standaloneElements.map((p) => ({ path: p.path, element: createElement(p.element) })),

  // Public status / error / invite pages
  { path: '/401', element: <Page401 /> },
  { path: '/403', element: <Page403 /> },
  { path: '/404', element: <Page404 /> },
  { path: '/429', element: <Page429 /> },
  { path: '/500', element: <Page500 /> },
  { path: '/503', element: <Page503 /> },
  { path: '/access-denied', element: <AccessDenied /> },
  { path: '/account-locked', element: <AccountLocked /> },
  { path: '/accept-invite/:token', element: <AcceptInvite /> },

  // Error boundary + 404
  {
    path: '*',
    element: <ErrorBoundary><NotFound /></ErrorBoundary>,
  },
]);

// Throwaway export so the tree stays tree-shakeable for tests/SSR.
export { RouterProvider };

// Route name constants for navigation and tests
export const routes = {
  HOME: '/',
  SIGNIN: '/signin',
  SIGNUP: '/signup',
  PUBLIC_PROFILE: '/@:handle',
  DASHBOARD: '/dashboard',
  SETTINGS_PROFILE: '/settings/profile',
  SETTINGS_SECURITY: '/settings/security',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  ADMIN_USERS: '/admin/users',
  APP: '/app',
  APP_TENANT: '/app/:tenantSlug/*',
  BRANDS: '/app/brands',
  BRAND_SAFETY: '/app/brand-safety',
  CAMPAIGNS: '/app/campaigns',
  CONTENT: '/app/content',
  CREATORS: '/app/creators',
  DELIVERABLES: '/app/deliverables',
  DISPUTES: '/app/disputes',
  DOCUMENTS: '/app/documents',
  FILES: '/app/files',
  FINANCE: '/app/finance',
  HELP: '/app/help',
  INTEGRATIONS: '/app/integrations',
  ISSUES: '/app/issues',
  ONBOARDING: '/app/onboarding',
  PRIVACY: '/app/privacy',
  PROGRAMMES: '/app/programmes',
  REPORTS: '/app/reports',
  RIGHTS: '/app/rights',
  SECURITY: '/app/security',
  TEAMS: '/app/teams',
  TENANTS: '/app/tenants',
  TOOLS: '/app/tools',
  WORKSPACES: '/app/workspaces',
  AGREEMENTS: '/app/agreements',
  OFFERS: '/app/offers',
  ORGANISATIONS: '/app/organisations',
  PARTIES: '/app/parties',
  SETTLEMENTS: '/app/settlements',
  PAYMENTS: '/app/payments',
  PAYMENT_ACCOUNTS: '/app/payment-accounts',
  SOCIAL_ACCOUNTS: '/app/social-accounts',
  WEBHOOKS: '/app/webhooks',
  API_KEYS: '/app/api-keys',
  SUBSCRIPTIONS: '/app/subscriptions',
  TAX: '/app/tax',
  NOTIFICATIONS: '/app/notifications',
  AUDIT_LOGS: '/app/audit-logs',
  CONSENTS: '/app/consents',
  DOCS: '/app/docs',
  BACKUP: '/app/backup',
  MONITORING: '/app/monitoring',
  SEARCH: '/app/search',
  BREACH_DETECTION: '/app/breach-detection',
  HEALTH_CHECK: '/health-check',
  NOT_FOUND: '*',
};